import { ButtonAppearance, Size } from "../../types";
import {
  ComponentPropsWithoutRef,
  Ref,
  forwardRef,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTrackingId } from "../../hooks";
import { DataTrackingId, LayoutUtilProps } from "../../types";
import { Icon, IconProps } from "../Icon";
import { childrenToString } from "../../internal/functions";
import Spinner from "../Spinner";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { warnOnce } from "../../internal/functions/warnOnce";
import { AiMarkIconAnimated } from "../AiMark/internal/AiMarkIconAnimated";
import {
  type AiIconName,
  AI_ICON_MAP,
  ICON_ONLY_AI_SIZE_PX,
  aiMarkShowsAnimatedMark,
  iconHasLeadingSlot,
} from "./internal";

import cx from "classnames";
import styles from "./Button.module.scss";

export type { AiIconName };

/**
 * Props for the Button component
 * @extends Omit<ComponentPropsWithoutRef<"button">, "children">
 * @extends LayoutUtilProps
 * @extends DataTrackingId
 */
export type ButtonProps = Omit<ComponentPropsWithoutRef<"button">, "children"> &
  LayoutUtilProps &
  DataTrackingId & {
    /**
     * When `true` or an icon name, shows an AI indicator. With button text, any leading
     * `icon` (plain Svg or `icon.before`) is not shown—the AI icon is used instead.
     * On an icon-only button, `icon` is not shown—the AI icon is used instead.
     * Icon-only usage must include an accessible name (for example `aria-label`).
     * In development, a console warning is logged when `icon` is overridden.
     */
    aiMark?: boolean | AiIconName;

    /**
     * The visual variant of the button.
     * @default secondary
     */
    appearance?: ButtonAppearance;

    /**
     * The size of the button.
     * @default medium
     */
    size?: Extract<Size, "xsmall" | "small" | "medium" | "large">;

    /**
     * The loading state of the button.
     * If true, it will show infinite state of the loading.
     * @default false
     */
    loading?: boolean;
  } & (
    | {
        children?: ComponentPropsWithoutRef<"button">["children"];
        /**
         * The icons of the button.
         */
        icon?:
          | IconProps["svg"]
          | { after: IconProps["svg"] }
          | { before: IconProps["svg"] };
      }
    | {
        children?: never;
        /**
         * The icon of the icon only button.
         */
        icon?: IconProps["svg"];
      }
  );

/**
 * Button component for triggering actions and user interactions.
 *
 * Features:
 * - Multiple visual appearances (primary, secondary, ghost, danger variants)
 * - Four size options (xsmall, small, medium, large)
 * - Loading state with spinner animation
 * - Icon support (before, after, or icon-only)
 * - Optional AI mark / named AI icons via `aiMark`
 * - Full accessibility support with ARIA attributes
 * - Supports layout utilities for positioning and spacing
 * - Automatic disabled state when loading
 * - Flexible content with text and/or icons
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Button appearance="primary" size="medium" onClick={() => console.log('clicked')}>
 *   Click Me
 * </Button>
 */
export const Button = forwardRef(
  (props: ButtonProps, ref: Ref<HTMLButtonElement>) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

    const {
      aiMark,
      children,
      className,
      appearance = "secondary",
      size = "medium",
      icon,
      loading = false,
      disabled,
      style,
      type = "button",
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      ...rest
    } = componentProps;

    const aiOn = !!aiMark;
    const isIconOnly = !children;

    if (aiOn && icon) {
      if (isIconOnly) {
        warnOnce(
          "[Anvil2 Button] The `icon` prop is ignored when `aiMark` is set on an icon-only button. Only the AI icon is rendered.",
        );
      } else if (iconHasLeadingSlot(icon)) {
        warnOnce(
          "[Anvil2 Button] The leading `icon` (plain Svg or `icon.before`) is ignored when `aiMark` is set with button text. Use `icon={{ after: Svg }}` for a trailing icon, or omit `icon`.",
        );
      }
    }

    const data = {
      children: childrenToString(props.children),
      appearance,
      icon,
      size,
      type,
      aiMark,
    };

    const trackingId = useTrackingId({
      name: "Button",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const isAnimated = isHovered || isFocused;

    const inheritColorForMark =
      appearance !== "secondary" && appearance !== "ghost";

    const renderStandardLeading = useCallback(() => {
      if (typeof icon === "object" && icon !== null && "before" in icon) {
        return (
          <Icon
            className={styles["icon"]}
            inherit
            aria-hidden
            svg={icon.before}
          />
        );
      }
      if (icon && typeof icon !== "object") {
        return (
          <Icon
            className={styles["icon"]}
            inherit
            aria-hidden
            svg={icon}
            size={!children && !size.includes("small") ? "large" : "medium"}
          />
        );
      }
      return null;
    }, [icon, children, size]);

    const renderAiLeading = useCallback(() => {
      if (aiMarkShowsAnimatedMark(aiMark)) {
        return (
          <AiMarkIconAnimated
            animate={isAnimated}
            inheritColor={inheritColorForMark}
            size={isIconOnly ? ICON_ONLY_AI_SIZE_PX[size] : undefined}
            spin
          />
        );
      }
      const SvgIcon = AI_ICON_MAP[aiMark as Exclude<AiIconName, "mark">];
      return (
        <Icon
          className={styles["icon"]}
          inherit
          aria-hidden
          data-anv="ai-mark-icon"
          svg={SvgIcon}
          size={isIconOnly && !size.includes("small") ? "large" : "medium"}
        />
      );
    }, [aiMark, isAnimated, inheritColorForMark, isIconOnly, size]);

    const leadingContent = useMemo(() => {
      if (loading) {
        return (
          <Spinner inherit size="small" className={styles["loading-spinner"]} />
        );
      }
      if (aiOn) {
        return renderAiLeading();
      }
      return renderStandardLeading();
    }, [aiOn, loading, renderAiLeading, renderStandardLeading]);

    const trailingAfter =
      !isIconOnly && typeof icon === "object" && "after" in icon ? (
        <Icon className={styles["icon"]} inherit aria-hidden svg={icon.after} />
      ) : null;

    const buttonClassNames = cx(className, styles["button"], {
      [styles["appearance-primary"]]: appearance === "primary",
      [styles["appearance-secondary"]]: appearance === "secondary",
      [styles["appearance-ghost"]]: appearance === "ghost",
      [styles["danger-secondary"]]: appearance === "danger-secondary",
      [styles["danger-primary"]]: appearance === "danger",
      [styles["size-xsmall"]]: size === "xsmall",
      [styles["size-small"]]: size === "small",
      [styles["size-medium"]]: size === "medium",
      [styles["size-large"]]: size === "large",
      [styles["type-icon"]]: isIconOnly,
      [styles["loading"]]: loading,
    });

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        setIsHovered(true);
        onMouseEnter?.(e);
      },
      [onMouseEnter],
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        setIsHovered(false);
        onMouseLeave?.(e);
      },
      [onMouseLeave],
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLButtonElement>) => {
        setIsFocused(true);
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLButtonElement>) => {
        setIsFocused(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    return (
      <button
        data-tracking-id={trackingId}
        className={buttonClassNames}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading ? true : undefined}
        data-anv="button"
        style={styleCombined}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...rest}
      >
        {leadingContent}
        {children}
        {trailingAfter}
      </button>
    );
  },
);

Button.displayName = "Button";
